import {
  InsightEvidence,
  IssueTracker,
  type CallNoteMetadata,
  type CatalogueFeature,
  type DiscussionTopic,
  type DocumentArtifact,
  type GitCommitEvidence,
  type HydratedEvidenceDocument,
  type Insight,
  type IssueEvidence,
  type PullRequestEvidence,
  type ResolvedChange,
  type ResolvedChangeArtifact,
  type ResolvedPerson,
  type Signal,
  type Ticket,
} from "@devplaninc/codeup";
import {GitProvider} from "@/pb/api/devplan/types/integrations";
import {commitUrl, prUrl, repoProvider} from "@/app/util/gitUtil";
import {catalogueFeatureHref, catalogueFeatureLabel} from "@/app/util/catalogueFeatureUtil";
import {callNoteHref, callNoteTitle, pickDominantSignalSource, type SignalSourceKind} from "@/app/components/signals/signalKinds";
import {
  callNoteProviderKey,
  documentEvidenceKey,
  issueEvidenceKey,
} from "@/app/components/evidence/evidenceHydrationRefs";
import {artifactEvidenceProvider, documentEvidenceProvider, evidenceProviderLabel, type EvidenceProvider} from "@/app/components/evidence/evidenceProvider";
import {chatHrefFromTopic, chatProviderFromTopic, type ChatEvidenceProvider} from "@/app/components/evidence/slackEvidenceLinks";
import {
  insightKind,
  insightKindLabel,
  insightSubtypeLabel,
  insightTitle,
} from "@/app/components/insights/insight-display";

/**
 * Lookup tables used to hydrate evidence rows. `signals` / `changes` let us
 * resolve a signalId / changeId ref to its full content so the evidence row
 * can render the shared SignalCard / ChangeCard. `featureMap`,
 * `repoProviderMap`, and `companyId` are needed to render change artifacts
 * with the same components Pulse uses (`ArtifactBadges` in
 * changes-report/ChangeMetadataBadges). `personsById` surfaces attendee /
 * author names in card meta lines and the expanded People section.
 */
export interface EvidenceResolvers {
  signals?: Record<string, Signal>;
  changes?: Record<string, ResolvedChange>;
  personsById?: Record<string, ResolvedPerson>;
  featureMap?: Map<string, CatalogueFeature>;
  repoProviderMap?: Record<string, GitProvider>;
  preferredGitProvider?: GitProvider;
  companyId?: number;
  /** Hydrated DiscussionTopic entities keyed by id, used to surface provider-
   * native chat links on `discussionTopicId` evidence refs. */
  discussionTopics?: Record<string, DiscussionTopic>;
  /** Hydrated CallNoteMetadata entities keyed by internal id. Only legacy call_note_id refs should use this. */
  callNotesByInternalId?: Record<string, CallNoteMetadata>;
  /** Hydrated CallNoteMetadata entities keyed by provider + provider note id. */
  callNotesByProviderKey?: Record<string, CallNoteMetadata>;
  /** Hydrated issue tracker tickets keyed by evidence issue key. */
  tickets?: Record<string, Ticket>;
  /** Hydrated document metadata keyed by evidence document key. */
  documents?: Record<string, HydratedEvidenceDocument>;
  /** Hydrated insights keyed by insight id. */
  insights?: Record<string, Insight>;
  /** Whether the workspace has a Slack integration connected. When false,
   * Slack permalinks are suppressed so the row falls back to text-only. */
  slackConnected?: boolean;
  /** Enables native `slack://` evidence links instead of HTTPS permalinks. */
  slackNativeDeepLinks?: boolean;
}

export function mergeEvidenceResolvers(
  base: EvidenceResolvers | undefined,
  override: EvidenceResolvers | undefined,
): EvidenceResolvers {
  if (!override) return base ?? {};
  return {
    ...base,
    ...override,
    signals: {...base?.signals, ...override.signals},
    changes: {...base?.changes, ...override.changes},
    personsById: {...base?.personsById, ...override.personsById},
    discussionTopics: {...base?.discussionTopics, ...override.discussionTopics},
    callNotesByInternalId: {...base?.callNotesByInternalId, ...override.callNotesByInternalId},
    callNotesByProviderKey: {...base?.callNotesByProviderKey, ...override.callNotesByProviderKey},
    tickets: {...base?.tickets, ...override.tickets},
    documents: {...base?.documents, ...override.documents},
    insights: {...base?.insights, ...override.insights},
    featureMap: new Map([
      ...(base?.featureMap?.entries() ?? []),
      ...(override.featureMap?.entries() ?? []),
    ]),
    repoProviderMap: {...base?.repoProviderMap, ...override.repoProviderMap},
    preferredGitProvider: override.preferredGitProvider ?? base?.preferredGitProvider,
    slackConnected: override.slackConnected ?? base?.slackConnected,
    slackNativeDeepLinks: override.slackNativeDeepLinks ?? base?.slackNativeDeepLinks,
    companyId: override.companyId ?? base?.companyId,
  };
}

export type EvidenceKind =
  | "change"
  | "signal"
  | "insight"
  | "discussion_topic"
  | "call_note"
  | "feature"
  | "pull_request"
  | "git_commit"
  | "issue"
  | "document"
  | "text"
  | "unknown";

export interface EvidenceDisplay {
  kind: EvidenceKind;
  label: string;
  referenceText: string;
  /** Label for the reference row in detail views ("Issue", "Repo", "Thread", …). */
  referenceLabel?: string;
  provider?: EvidenceRefProvider;
  iconProvider?: EvidenceProvider;
  href?: string;
  note: string;
  /** Captured content of the underlying source (signal summary, change
   * description, thread summary, …) — what the agent actually saw. */
  body?: string;
  /** All people involved with the source entity ("Cooper Becker, Megan Yu");
   * for issues this is the assignee. */
  people?: string;
  /** When the source entity itself was captured — fallback timestamp for
   * evidence rows whose InsightEvidence wrapper has no createdAt. */
  occurredAt?: Date;
  /** Workflow status for issue-tracker evidence (e.g. "In Progress"). */
  status?: string;
  /** Linked items hydrated from the source entity (e.g. ResolvedChange.artifacts). */
  linkedItems?: ResolvedChangeArtifact[];
  /** Evidence attached to an aggregate entity, rendered as nested sub-items. */
  nestedEvidence?: InsightEvidence[];
  attributes?: Array<{label: string; value: string}>;
}

export type EvidenceRefProvider =
  | {source: "issue_tracker"; tracker: IssueTracker}
  | {source: "git_provider"; provider: GitProvider}
  | {source: "slack"}
  | {source: "teams"};

const KIND_LABELS: Record<EvidenceKind, string> = {
  change: "Change",
  signal: "Signal",
  insight: "Insight",
  discussion_topic: "Discussion",
  call_note: "Call note",
  feature: "Feature",
  pull_request: "Pull request",
  git_commit: "Commit",
  issue: "Issue",
  document: "Document",
  text: "Text note",
  unknown: "Unavailable source",
};

/** Feature refs describe what the work touched, not where it was observed —
 * they are not surfaced as evidence anywhere in the UI. */
export function displayableEvidence(evidence: InsightEvidence[]): InsightEvidence[] {
  return evidence.filter(item => item.ref?.target?.$case !== "featureId");
}

export function toEvidenceDisplay(evidence: InsightEvidence, resolvers?: EvidenceResolvers): EvidenceDisplay {
  const ref = evidence.ref?.target;
  const note = evidence.note ?? "";
  if (!ref) {
    if (note.trim()) {
      return {kind: "text", label: KIND_LABELS.text, referenceText: "", note};
    }
    return {kind: "unknown", label: KIND_LABELS.unknown, referenceText: "", note};
  }
  switch (ref.$case) {
    case "changeId": {
      const resolved = resolvers?.changes?.[ref.value];
      return {
        kind: "change",
        label: KIND_LABELS.change,
        referenceText: resolved?.summary?.trim() ?? "",
        body: changeBody(resolved),
        people: changePeople(resolved, resolvers?.personsById),
        occurredAt: resolved?.createdAt ?? resolved?.periodEnd,
        note,
        linkedItems: resolved?.artifacts,
      };
    }
    case "signalId": {
      const signal = resolvers?.signals?.[ref.value];
      return {
        kind: "signal",
        label: KIND_LABELS.signal,
        referenceText: signalReferenceText(signal, resolvers),
        body: signal?.summary?.trim() || undefined,
        people: signalPeople(signal, resolvers?.personsById),
        occurredAt: signal?.createdAt ?? signal?.periodEnd,
        note,
      };
    }
    case "insightId":
      return buildInsightDisplay(ref.value, note, resolvers?.insights?.[ref.value]);
    case "discussionTopicId": {
      const topic = resolvers?.discussionTopics?.[ref.value];
      const referenceText = topic?.title?.trim() ?? "";
      const chatProvider = chatProviderFromTopic(topic);
      const href = chatHrefFromTopic(topic, {nativeSlackDeepLinks: resolvers?.slackNativeDeepLinks});
      const provider = chatProvider ? {source: chatProvider} as const : undefined;
      return {
        kind: "discussion_topic",
        label: evidenceProviderLabel(chatProvider ?? "discussion"),
        referenceText,
        referenceLabel: "Thread",
        body: topic?.summary?.trim() || undefined,
        people: topicPeople(topic),
        occurredAt: topicPostedAt(topic),
        note,
        href,
        provider,
        iconProvider: chatProvider,
      };
    }
    case "callNoteId": {
      const callNote = resolvers?.callNotesByInternalId?.[ref.value];
      const title = callNoteTitle(callNote);
      const provider = callNoteProvider(callNote?.providerType);
      return {
        kind: "call_note",
        label: evidenceProviderLabel(provider),
        referenceText: title,
        referenceLabel: "Call",
        people: callNotePeople(callNote),
        occurredAt: callNote?.noteCreatedAt ?? callNote?.createdAt,
        iconProvider: provider,
        href: callNoteHref(callNote),
        note,
      };
    }
    case "callNote": {
      const callNote = resolvers?.callNotesByProviderKey?.[callNoteProviderKey(ref.value.providerType, ref.value.noteId)];
      const title = callNoteTitle(callNote) || ref.value.noteId;
      const provider = callNoteProvider(callNote?.providerType || ref.value.providerType);
      return {
        kind: "call_note",
        label: evidenceProviderLabel(provider),
        referenceText: title,
        referenceLabel: "Call",
        people: callNotePeople(callNote),
        occurredAt: callNote?.noteCreatedAt ?? callNote?.createdAt,
        iconProvider: provider,
        href: callNoteHref(callNote),
        note,
      };
    }
    case "featureId": {
      const feature = resolvers?.featureMap?.get(ref.value);
      const href = catalogueFeatureHref(resolvers?.companyId, ref.value);
      return {
        kind: "feature",
        label: KIND_LABELS.feature,
        referenceText: catalogueFeatureLabel(feature) ?? "",
        referenceLabel: "Feature",
        note,
        href,
      };
    }
    case "pullRequest":
      return buildPullRequestDisplay(ref.value, note, resolvers?.repoProviderMap);
    case "gitCommit":
      return buildGitCommitDisplay(ref.value, note, resolvers?.repoProviderMap);
    case "issue":
      return buildIssueDisplay(ref.value, note, resolvers);
    case "document":
      return buildDocumentDisplay(ref.value, note, resolvers?.documents?.[documentEvidenceKey(ref.value)]);
    case "discussionTopic": {
      const topic = resolvers?.discussionTopics?.[ref.value.topicId];
      const referenceText = topic?.title?.trim() || ref.value.topicId;
      const chatProvider = explicitChatProvider(ref.value.provider) ?? chatProviderFromTopic(topic);
      const href = chatHrefFromTopic(topic, {nativeSlackDeepLinks: resolvers?.slackNativeDeepLinks});
      const provider = chatProvider ? {source: chatProvider} as const : undefined;
      return {
        kind: "discussion_topic",
        label: evidenceProviderLabel(chatProvider ?? "discussion"),
        referenceText,
        referenceLabel: "Thread",
        body: topic?.summary?.trim() || undefined,
        people: topicPeople(topic),
        occurredAt: topicPostedAt(topic),
        note,
        href,
        provider,
        iconProvider: chatProvider,
      };
    }
    case "text":
      return {kind: "text", label: KIND_LABELS.text, referenceText: ref.value ?? "", body: ref.value?.trim() || undefined, note};
  }
}

/**
 * Resolve a single evidence item to the provider that backs it — the icon /
 * label identity shared by pills, stacked source icons, and the detail
 * flyout. Hydrated entities sharpen the answer (e.g. a signalId resolves to
 * its dominant artifact source, an issue to its tracker).
 */
export function evidenceItemProvider(evidence: InsightEvidence, resolvers?: EvidenceResolvers): EvidenceProvider {
  const target = evidence.ref?.target;
  if (!target) return evidence.note?.trim() ? "text_note" : "unknown";
  switch (target.$case) {
    case "signalId":
      return signalProvider(resolvers?.signals?.[target.value]);
    case "changeId":
      return gitProviderEvidenceProvider(resolvers?.preferredGitProvider) ?? "change";
    case "insightId":
      return "insight";
    case "discussionTopicId": {
      const topic = resolvers?.discussionTopics?.[target.value];
      return chatProviderFromTopic(topic) ?? "discussion";
    }
    case "callNoteId":
      return callNoteProvider(resolvers?.callNotesByInternalId?.[target.value]?.providerType);
    case "callNote":
      return callNoteProvider(resolvers?.callNotesByProviderKey?.[callNoteProviderKey(target.value.providerType, target.value.noteId)]?.providerType || target.value.providerType);
    case "featureId":
      return "feature";
    case "pullRequest":
    case "gitCommit":
      return repoEvidenceProvider(target.value.repo, resolvers?.repoProviderMap);
    case "issue":
      return issueProvider(target.value.tracker, resolvers?.tickets?.[issueEvidenceKey(target.value)]);
    case "document":
      return documentEvidenceProvider(target.value.provider);
    case "discussionTopic":
      return explicitChatProvider(target.value.provider) ?? "discussion";
    case "text":
      return "text_note";
  }
}

/**
 * All distinct providers behind a single evidence item, most frequent first.
 * Aggregate refs fan out to their underlying artifacts: a change lists the
 * sources of its PRs / commits / issues / threads, a signal the sources of
 * its artifacts. Scalar refs resolve to a single provider.
 */
export function evidenceItemProviders(evidence: InsightEvidence, resolvers?: EvidenceResolvers): EvidenceProvider[] {
  const target = evidence.ref?.target;
  if (target?.$case === "changeId") {
    const providers = changeArtifactProviders(resolvers?.changes?.[target.value], resolvers);
    if (providers.length > 0) return providers;
  }
  if (target?.$case === "signalId") {
    const signal = resolvers?.signals?.[target.value];
    if (signal && signal.artifacts.length > 0) {
      const providers = orderedUniqueProviders(signal.artifacts.map(artifactEvidenceProvider))
        .filter(provider => provider !== "unknown" && provider !== "feature");
      if (providers.length > 0) return providers;
    }
  }
  return [evidenceItemProvider(evidence, resolvers)];
}

function changeArtifactProviders(change: ResolvedChange | undefined, resolvers?: EvidenceResolvers): EvidenceProvider[] {
  if (!change) return [];
  const providers: EvidenceProvider[] = [];
  for (const artifact of change.artifacts) {
    const target = artifact.target;
    if (!target) continue;
    switch (target.$case) {
      case "pullRequest":
      case "gitCommit":
        providers.push(changeRepoProvider(target.value.repo, resolvers));
        break;
      case "issue":
        providers.push(issueProvider(target.value.tracker));
        break;
      // Feature artifacts are intentionally not surfaced as source icons —
      // they describe what the change touched, not where it was observed.
    }
  }
  return orderedUniqueProviders(providers);
}

function changeRepoProvider(repo: string, resolvers?: EvidenceResolvers): EvidenceProvider {
  const mapped = resolvers?.repoProviderMap?.[repo.trim()];
  return gitProviderEvidenceProvider(mapped ?? resolvers?.preferredGitProvider) ?? "github";
}

function orderedUniqueProviders(providers: EvidenceProvider[]): EvidenceProvider[] {
  const counts = new Map<EvidenceProvider, number>();
  for (const provider of providers) {
    counts.set(provider, (counts.get(provider) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([provider]) => provider);
}

/** Providers for a list of evidence items (one per underlying source); falls
 * back to ["unknown"] so icon stacks always have something to render. */
export function evidenceProviders(evidence: InsightEvidence[], resolvers?: EvidenceResolvers): EvidenceProvider[] {
  const providers = evidence.flatMap(item => evidenceItemProviders(item, resolvers));
  return providers.length > 0 ? providers : ["unknown"];
}

function signalProvider(signal: Signal | undefined): EvidenceProvider {
  if (!signal) return "signal";
  return signalSourceProvider(pickDominantSignalSource(signal));
}

function buildInsightDisplay(id: string, note: string, insight: Insight | undefined): EvidenceDisplay {
  const kind = insight ? insightKind(insight) : "unknown";
  const kindLabel = insightKindLabel(kind);
  const subtype = insight ? insightSubtypeLabel(insight) : undefined;
  return {
    kind: "insight",
    label: kindLabel,
    referenceText: insight ? insightTitle(insight) : "Insight",
    referenceLabel: "Insight",
    body: insight?.description?.trim() || undefined,
    occurredAt: insight?.createdAt ?? insight?.updatedAt,
    note,
    iconProvider: "insight",
    nestedEvidence: insight?.evidence,
    attributes: [
      {label: "Type", value: kindLabel},
      subtype ? {label: kind === "risk" ? "Severity" : "Detail", value: subtype} : undefined,
    ].filter((row): row is {label: string; value: string} => Boolean(row)),
  };
}

function signalSourceProvider(kind: SignalSourceKind): EvidenceProvider {
  switch (kind) {
    case "call_note":
      return "call_note";
    case "discussion_topic":
      return "discussion";
    case "document":
      return "document";
    case "feature":
      return "feature";
    case "jira":
      return "jira";
    case "linear":
      return "linear";
    case "ticket":
      return "ticket";
    case "event":
      return "unknown";
  }
}

function callNoteProvider(providerType: string | undefined): EvidenceProvider {
  switch (providerType?.trim().toLowerCase()) {
    case "teams_meeting":
      return "teams";
    case "zoom":
      return "zoom";
    case "granola":
      return "granola";
    case "otter":
      return "otter";
    default:
      return "call_note";
  }
}

function issueProvider(tracker: IssueTracker, ticket?: Ticket): EvidenceProvider {
  const resolved = ticket?.summary?.tracker ?? tracker;
  switch (resolved) {
    case IssueTracker.ISSUE_TRACKER_JIRA:
      return "jira";
    case IssueTracker.ISSUE_TRACKER_LINEAR:
      return "linear";
    default:
      return "ticket";
  }
}

function repoEvidenceProvider(repo: string, providerMap: Record<string, GitProvider> | undefined): EvidenceProvider {
  return gitProviderEvidenceProvider(repoProvider(repo.trim(), providerMap)) ?? "github";
}

export function gitProviderEvidenceProvider(provider: GitProvider | undefined): EvidenceProvider | undefined {
  switch (provider) {
    case GitProvider.GITHUB:
      return "github";
    case GitProvider.BIT_BUCKET:
      return "bitbucket";
    case GitProvider.GITLAB:
      return "gitlab";
    default:
      return undefined;
  }
}

// The change summary surfaces directly on the evidence row (referenceText);
// the body holds only the longer description shown behind the Details toggle.
function changeBody(change: ResolvedChange | undefined): string | undefined {
  return change?.description?.trim() || undefined;
}

// codeup.changes.PersonRole values, mirrored locally because the enum is not
// re-exported from the @devplaninc/codeup package index.
const PERSON_ROLE_AUTHOR = 1;
const PERSON_ROLE_COMMITTER = 3;

// All distinct people on the change, authors and committers first.
function changePeople(
  change: ResolvedChange | undefined,
  personsById: Record<string, ResolvedPerson> | undefined,
): string | undefined {
  if (!change || !personsById) return undefined;
  const ordered = [
    ...change.people.filter(p => p.role === PERSON_ROLE_AUTHOR),
    ...change.people.filter(p => p.role === PERSON_ROLE_COMMITTER),
    ...change.people,
  ];
  return joinNames(ordered.map(personRef => personName(personsById[personRef.personId])));
}

function signalPeople(
  signal: Signal | undefined,
  personsById: Record<string, ResolvedPerson> | undefined,
): string | undefined {
  if (!signal || !personsById) return undefined;
  return joinNames(signal.personIds.map(id => personName(personsById[id])));
}

function signalReferenceText(signal: Signal | undefined, resolvers?: EvidenceResolvers): string {
  return signalDocumentTitle(signal, resolvers) || signal?.name?.trim() || "";
}

function signalDocumentTitle(signal: Signal | undefined, resolvers?: EvidenceResolvers): string | undefined {
  if (!signal || !resolvers?.documents) return undefined;
  for (const artifact of signal.artifacts) {
    if (artifact.details?.$case !== "document") continue;
    const document = artifact.details.value;
    const title = resolvers.documents[documentEvidenceKey(document)]?.title?.trim();
    if (title && title !== document.documentId.trim()) return title;
  }
  return undefined;
}

function joinNames(names: Array<string | undefined>): string | undefined {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of names) {
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out.length > 0 ? out.join(", ") : undefined;
}

function personName(person: ResolvedPerson | undefined): string | undefined {
  if (!person) return undefined;
  const name = person.name?.trim();
  if (name) return name;
  const email = person.email?.trim();
  if (email) return email;
  return person.gitUsername?.trim() || undefined;
}

function topicPostedAt(topic: DiscussionTopic | undefined): Date | undefined {
  for (const ref of topic?.sourceRefs ?? []) {
    if (ref.ref?.$case !== "slack" && ref.ref?.$case !== "teams") continue;
    if (ref.ref.value.postedAt) return ref.ref.value.postedAt;
  }
  return topic?.periodEnd;
}

function topicPeople(topic: DiscussionTopic | undefined): string | undefined {
  const names = (topic?.sourceRefs ?? [])
    .map(ref => ref.ref?.$case === "slack" || ref.ref?.$case === "teams"
      ? ref.ref.value.authorName?.trim()
      : undefined);
  return joinNames(names);
}

function callNotePeople(note: CallNoteMetadata | undefined): string | undefined {
  const details = note?.noteDetails;
  if (details?.$case !== "granola" && details?.$case !== "teamsMeeting" && details?.$case !== "zoom") return undefined;
  return joinNames([
    details.value.ownerName?.trim(),
    ...details.value.attendeeNames.map(name => name.trim()),
  ]);
}

function buildPullRequestDisplay(
  pr: PullRequestEvidence,
  note: string,
  providerMap: Record<string, GitProvider> | undefined,
): EvidenceDisplay {
  const repo = pr.repo?.trim();
  const number = pr.number;
  if (!repo || !number) {
    return {
      kind: "pull_request",
      label: KIND_LABELS.pull_request,
      referenceText: repo ? `${repoShortName(repo)}${number ? `#${number}` : ""}` : "",
      referenceLabel: "Pull request",
      note,
    };
  }
  const provider = repoProvider(repo, providerMap);
  return {
    kind: "pull_request",
    label: KIND_LABELS.pull_request,
    referenceText: `${repoShortName(repo)}#${number}`,
    referenceLabel: "Pull request",
    href: prUrl(repo, number, provider),
    provider: gitProviderDisplay(provider, repo, providerMap),
    note,
  };
}

/** "org/repo" → "repo"; display only — links keep the full path. */
function repoShortName(repo: string): string {
  const slash = repo.lastIndexOf("/");
  return slash >= 0 ? repo.slice(slash + 1) : repo;
}

function buildGitCommitDisplay(
  commit: GitCommitEvidence,
  note: string,
  providerMap: Record<string, GitProvider> | undefined,
): EvidenceDisplay {
  const repo = commit.repo?.trim();
  const sha = commit.sha?.trim();
  if (!repo || !sha) {
    return {
      kind: "git_commit",
      label: KIND_LABELS.git_commit,
      referenceText: repo ? repoShortName(repo) : "",
      referenceLabel: "Commit",
      note,
    };
  }
  const provider = repoProvider(repo, providerMap);
  return {
    kind: "git_commit",
    label: KIND_LABELS.git_commit,
    referenceText: `${repoShortName(repo)}@${sha.slice(0, 7)}`,
    referenceLabel: "Commit",
    href: commitUrl(repo, sha, provider),
    provider: gitProviderDisplay(provider, repo, providerMap),
    note,
  };
}

function buildIssueDisplay(issue: IssueEvidence, note: string, resolvers?: EvidenceResolvers): EvidenceDisplay {
  const ticket = resolvers?.tickets?.[issueEvidenceKey(issue)];
  const label = issueTrackerLabel(issue.tracker);
  const referenceText = issueEvidenceReferenceText(issue, ticket);
  const status = ticket?.summary?.status?.trim() || undefined;
  const assignee = issueAssigneeLabel(ticket);
  return {
    kind: "issue",
    label,
    referenceText,
    referenceLabel: "Issue",
    occurredAt: ticket?.summary?.updatedAt ?? ticket?.summary?.createdAt,
    href: issueEvidenceHref(issue, ticket),
    body: ticket?.description?.trim() || undefined,
    people: assignee,
    status,
    note,
    provider: issueTrackerProvider(issue.tracker),
  };
}

function issueAssigneeLabel(ticket: Ticket | undefined): string | undefined {
  const assignee = ticket?.summary?.assignee;
  if (!assignee) return undefined;
  return assignee.name?.trim() || assignee.email?.trim() || assignee.id?.trim() || undefined;
}

export function issueEvidenceReferenceText(issue: IssueEvidence, ticket?: Ticket): string {
  const identifier = issueEvidenceIdentifier(issue, ticket);
  const title = ticket?.summary?.title?.trim();
  if (identifier && title && title !== identifier) return `${identifier} - ${title}`;
  return title || identifier;
}

export function issueEvidenceIdentifier(issue: IssueEvidence, ticket?: Ticket): string {
  const hydratedId = ticket?.summary?.id?.trim();
  if (hydratedId) return hydratedId;

  const urlIdentifier = issueIdentifierFromUrl(issue, issueEvidenceUrlCandidate(issue));
  if (urlIdentifier) return urlIdentifier;

  const project = issue.project?.trim();
  const id = issue.id?.trim();
  if (!project || !id) return id || project || "";
  if (id.toUpperCase().startsWith(`${project.toUpperCase()}-`)) return id;
  return `${project}-${id}`;
}

export function issueEvidenceHref(issue: IssueEvidence, ticket?: Ticket): string | undefined {
  return ticket?.url?.trim() || issueEvidenceUrlCandidate(issue);
}

function issueEvidenceUrlCandidate(issue: IssueEvidence): string | undefined {
  const candidates = [issue.id, issue.project];
  return candidates
    .map(candidate => candidate?.trim())
    .find(candidate => candidate ? /^https?:\/\//i.test(candidate) : false);
}

function issueIdentifierFromUrl(issue: IssueEvidence, url: string | undefined): string | undefined {
  if (!url) return undefined;
  const pattern = issue.tracker === IssueTracker.ISSUE_TRACKER_JIRA
    ? /\/browse\/([^/?#]+)/i
    : issue.tracker === IssueTracker.ISSUE_TRACKER_LINEAR
      ? /\/issue\/([A-Z][A-Z0-9]*-\d+)/i
      : undefined;
  const match = pattern?.exec(url);
  return match?.[1]?.trim() || undefined;
}

function buildDocumentDisplay(document: DocumentArtifact, note: string, hydrated?: HydratedEvidenceDocument): EvidenceDisplay {
  const href = hydrated?.url?.trim() || undefined;
  const provider = documentEvidenceProvider(hydrated?.provider || document.provider);
  const hydratedTitle = hydrated?.title?.trim();
  const fallback = evidenceProviderLabel(provider);
  const title = hydratedTitle && hydratedTitle !== document.documentId.trim() ? hydratedTitle : fallback;
  return {
    kind: "document",
    label: evidenceProviderLabel(provider),
    referenceText: title,
    referenceLabel: "Document",
    href,
    note,
    iconProvider: provider,
  };
}

function issueTrackerLabel(tracker: IssueTracker): string {
  return ISSUE_TRACKER_LABELS[tracker] ?? KIND_LABELS.issue;
}

function issueTrackerProvider(tracker: IssueTracker): EvidenceDisplay["provider"] {
  if (!ISSUE_TRACKER_LABELS[tracker]) return undefined;
  return {source: "issue_tracker", tracker};
}

const ISSUE_TRACKER_LABELS: Partial<Record<IssueTracker, string>> = {
  [IssueTracker.ISSUE_TRACKER_JIRA]: "Jira",
  [IssueTracker.ISSUE_TRACKER_LINEAR]: "Linear",
};

function gitProviderDisplay(
  provider: GitProvider,
  repo: string,
  providerMap: Record<string, GitProvider> | undefined,
): EvidenceDisplay["provider"] {
  if (!providerMap?.[repo]) return undefined;
  return {source: "git_provider", provider};
}

function explicitChatProvider(provider: string): ChatEvidenceProvider | undefined {
  switch (provider.trim().toLowerCase()) {
    case "slack":
      return "slack";
    case "teams":
      return "teams";
    default:
      return undefined;
  }
}
